import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ContentSection, PageFrame, PageHero } from '../components/PageBlocks';
import { fetchNewsPostBySlug, type NewsPost } from '../lib/supabase';

const formatDate = (iso: string) => {
    try {
        return new Date(iso).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        }).toUpperCase();
    } catch {
        return '';
    }
};

const renderBody = (body: string) => {
    const paragraphs = body.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
    return paragraphs.map((para, i) => (
        <p key={i} className="news-article__paragraph">
            {para}
        </p>
    ));
};

const NewsArticlePage = () => {
    const { slug = '' } = useParams();
    const [post, setPost] = useState<NewsPost | null | 'missing'>(null);

    useEffect(() => {
        let alive = true;
        fetchNewsPostBySlug(slug).then((data) => {
            if (!alive) return;
            setPost(data ?? 'missing');
        });
        return () => {
            alive = false;
        };
    }, [slug]);

    if (post === null) {
        return (
            <PageFrame>
                <ContentSection sectionId="loading" eyebrow="Loading" title="Fetching article…" borderTop={false}>
                    <p className="news-empty">Loading…</p>
                </ContentSection>
            </PageFrame>
        );
    }

    if (post === 'missing') {
        return (
            <PageFrame>
                <PageHero
                    sectionId="overview"
                    variant="minimal"
                    numeral="X"
                    eyebrow="Not found"
                    title="This piece is not published."
                    intro="The article you are looking for either does not exist or has been unpublished."
                />
                <ContentSection sectionId="back" eyebrow="Back" title="Return to signal" borderTop={false}>
                    <Link to="/news" className="news-item__link">
                        ← Back to News
                    </Link>
                </ContentSection>
            </PageFrame>
        );
    }

    const isPaper = post.category === 'publishing';

    return (
        <PageFrame>
            <PageHero
                sectionId="overview"
                variant="minimal"
                numeral={isPaper ? 'P' : 'S'}
                eyebrow={isPaper ? 'Publishing' : 'Signal'}
                title={post.title}
                intro={post.excerpt ?? ''}
                proofStrip={[
                    formatDate(post.published_at),
                    post.source_name ? `Original: ${post.source_name}` : 'Aven Studios',
                    'Our take — written on site.',
                ]}
            />

            {post.image_url && (
                <ContentSection sectionId="media" eyebrow="" title="" borderTop={false}>
                    <div className="news-article__media">
                        <img src={post.image_url} alt="" loading="eager" />
                    </div>
                </ContentSection>
            )}

            <ContentSection
                sectionId="take"
                eyebrow="Our take"
                title="Why this matters for local-first"
                borderTop={false}
            >
                <div className="news-article__body">
                    {post.body ? (
                        renderBody(post.body)
                    ) : (
                        <p className="news-article__paragraph">
                            {post.excerpt ??
                                'Our full commentary on this item is being written. The original source is linked below.'}
                        </p>
                    )}
                </div>
            </ContentSection>

            {post.source_url && (
                <ContentSection sectionId="source" eyebrow="Source" title="Read the original reporting">
                    <p className="news-article__source">
                        <a
                            href={post.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="news-item__link"
                        >
                            {post.source_name || 'Read original'} →
                        </a>
                    </p>
                </ContentSection>
            )}

            <ContentSection sectionId="back" eyebrow="Continue" title="More signal">
                <Link to="/news" className="news-item__link">
                    ← Back to News
                </Link>
            </ContentSection>
        </PageFrame>
    );
};

export default NewsArticlePage;
